import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, Smartphone } from 'lucide-react';
import { FilterState } from '../types';
import { POPULAR_BRANDS } from '../config/siteConfig';

interface SearchAssistantProps {
    isOpen: boolean;
    onClose: () => void;
    onSearch: (filters: FilterState) => void;
}

const SearchAssistant: React.FC<SearchAssistantProps> = ({ isOpen, onClose, onSearch }) => {
    const [step, setStep] = useState<1 | 2>(1);
    const [filters, setFilters] = useState<FilterState>({ year: '', make: '', model: '' });

    const handleSelect = (key: keyof FilterState, value: string) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        if (step < 2) {
            setStep(prev => (prev + 1) as 1 | 2);
        } else {
            onSearch({ ...filters, [key]: value });
            onClose();
            // Reset for next time after a delay
            setTimeout(() => {
                setStep(1);
                setFilters({ year: '', make: '', model: '' });
            }, 500);
        }
    };

    // Get models dynamically from configuration
    const getModels = (make: string) => {
        const brand = POPULAR_BRANDS.find(b => b.name === make);
        return brand ? brand.popularModels : ['Modelo General'];
    };

    // Get makes dynamically from configuration
    const availableMakes = POPULAR_BRANDS.map(b => b.name);

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[60]"
                    />

                    {/* Drawer/Modal */}
                    <motion.div
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        className="fixed right-0 top-0 h-full w-full max-w-md bg-zinc-950 border-l border-zinc-800 z-[70] shadow-2xl flex flex-col"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between p-6 border-b border-zinc-800">
                            <div className="flex items-center gap-3">
                                <div className="h-10 w-10 bg-white/10 rounded flex items-center justify-center border border-white/20">
                                    <Smartphone className="text-white h-6 w-6" />
                                </div>
                                <div>
                                    <h3 className="text-white font-display font-bold text-xl uppercase">Asistente</h3>
                                    <p className="text-gray-500 text-xs">Filtro de Compatibilidad</p>
                                </div>
                            </div>
                            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
                                <X className="h-6 w-6" />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto p-6">
                            <div className="mb-8">
                                <div className="flex items-center justify-between mb-4">
                                    <span className={`text-sm font-bold uppercase ${step === 1 ? 'text-white' : 'text-gray-600'}`}>01. Marca</span>
                                    <div className="w-8 h-[1px] bg-zinc-800" />
                                    <span className={`text-sm font-bold uppercase ${step === 2 ? 'text-white' : 'text-gray-600'}`}>02. Modelo</span>
                                </div>

                                <h2 className="text-2xl font-display font-bold text-white mb-2">
                                    {step === 1 && "Selecciona la marca"}
                                    {step === 2 && "¿Cuál es tu modelo?"}
                                </h2>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                {step === 1 && availableMakes.map(make => (
                                    <button
                                        key={make}
                                        onClick={() => handleSelect('make', make)}
                                        className="p-4 bg-zinc-900 border border-zinc-800 hover:border-white hover:bg-zinc-800 transition-all text-left group"
                                    >
                                        <span className="text-lg font-display font-bold text-gray-300 group-hover:text-white">{make}</span>
                                    </button>
                                ))}

                                {step === 2 && filters.make && getModels(filters.make).map(model => (
                                    <button
                                        key={model}
                                        onClick={() => handleSelect('model', model)}
                                        className="p-4 bg-zinc-900 border border-zinc-800 hover:border-white hover:bg-zinc-800 transition-all text-left group w-full col-span-2 flex justify-between items-center"
                                    >
                                        <span className="text-lg font-display font-bold text-gray-300 group-hover:text-white">{model}</span>
                                        <ChevronRight className="h-5 w-5 text-gray-600 group-hover:text-white" />
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="p-6 border-t border-zinc-800 bg-zinc-900/50">
                            {step > 1 && (
                                <button
                                    onClick={() => setStep(prev => (prev - 1) as 1 | 2)}
                                    className="text-gray-400 hover:text-white text-sm font-display uppercase tracking-wider"
                                >
                                    ← Volver atrás
                                </button>
                            )}
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default SearchAssistant;
